import type { MapDef, PoiKind, WorldObject } from '../world/mapdef';
import { objectsOn, MAPS } from '../world/map';
import { dayId } from '../core/state';
import { S } from '../core/game';
import { m } from '../i18n';
import { mapName, objName } from '../i18n/labels';
import { T } from '../world/tiles';

/**
 * Points of interest for the map panel: objects that declare a `poi` kind, plus the older
 * objects listed here, plus every door. Each one can be pinned so the guide arrow leads there.
 */
const POI_OF: Record<string, PoiKind> = {
  stTicket: 'transport', stTrain: 'transport', tpTrain: 'transport', hlTrain: 'transport', mrtRide: 'transport', mrtMap: 'info',
  xy_mrtSys: 'transport', xy_mrt101: 'transport', muExit: 'transport', hc_shuttle: 'transport', tk_shuttleTaroko: 'transport', tk_shuttleBeach: 'transport',
  busStop: 'transport', busStopW: 'transport', busStopE: 'transport', twElevator: 'sight', xy_easycard: 'info',
  ubike: 'rental', tk_bikeRent: 'rental', hc_bikePath: 'rental',
  toilet: 'toilet', bath: 'toilet',
  shopCounter: 'shop', cafeCounter: 'food', giftCounter: 'shop', deptCounter: 'shop', restCounter: 'food', life_fastfood: 'food', vending: 'food', canteen: 'food',
  tpNoodles: 'food', tpCakes: 'food', hlMochi: 'food', hlPancake: 'food', hlWonton: 'food', tk_mochi: 'food', tk_sausage: 'food',
  mkTea: 'food', mkChicken: 'food', mkBalls: 'food', mkTofu: 'food', mkOyster: 'food', mkIce: 'food', mkFish: 'food',
  htDesk: 'hotel', htBed: 'hotel', nurseDesk: 'info', libShelf: 'info', hlHelmet: 'info', tk_visitor: 'info', ap_info: 'info',
  pool: 'sight', fountain: 'sight', mallFountain: 'sight', pond: 'sight', hlView: 'sight', hlSea: 'sight', hlQingshui: 'sight', tk_shrine: 'sight', tk_sunrise: 'sight',
  muCabbage: 'sight', muMeat: 'sight', hlWhale: 'sight', piano: 'sight', easel: 'sight',
};

export interface Poi {
  id: string;
  kind: PoiKind | 'door';
  icon: string;
  label: string;
  x: number;
  y: number;
}

export const POI_ICON: Record<Poi['kind'], string> = { transport: '🚉', rental: '🛴', toilet: '🚻', bank: '🏦', food: '🍜', shop: '🛍️', sight: '📷', hotel: '🏨', info: 'ℹ️', door: '🚪' };
export const POI_ORDER: Poi['kind'][] = ['transport', 'door', 'rental', 'bank', 'toilet', 'food', 'shop', 'sight', 'hotel', 'info'];

export function poiKind(o: WorldObject): PoiKind | null {
  return o.poi ?? POI_OF[o.id] ?? null;
}

/** Points of interest on a map, sorted by kind. */
export function poisOf(map: MapDef): Poi[] {
  const out: Poi[] = [];
  for (const o of objectsOn(map.id, dayId(S()))) {
    const kind = poiKind(o);
    if (!kind) continue;
    out.push({ id: o.id, kind, icon: o.icon, label: objName(o.label), x: o.rect.x + o.rect.w / 2, y: o.rect.y + o.rect.h });
  }
  // Doors and the arrival points of other maps' doors into this one.
  const seen = new Set<string>();
  for (const d of map.doors) {
    if (seen.has(d.to)) continue;
    seen.add(d.to);
    const [tx, ty] = d.tiles[0];
    out.push({ id: `door:${d.to}`, kind: 'door', icon: '🚪', label: m().airport.mapUi.door(mapName(d.to)), x: tx * T + (d.tiles.length > 1 ? T : T / 2), y: ty * T + T / 2 });
  }
  out.sort((a, b) => POI_ORDER.indexOf(a.kind) - POI_ORDER.indexOf(b.kind) || a.label.localeCompare(b.label));
  return out;
}

/** The places on the Taiwan overview (and Barcelona beyond), with the map id each one opens. */
export interface Place {
  id: keyof typeof MAPS;
  /** Position on the island drawing, in [0, 1] of the overview canvas. */
  x: number;
  y: number;
  icon: string;
  /** Which object on which map starts the journey there (for the pin). */
  via: { map: keyof typeof MAPS; id: string } | null;
}

export const PLACES: readonly Place[] = [
  { id: 'town', x: 0.62, y: 0.3, icon: '🏠', via: null },
  { id: 'taipei', x: 0.56, y: 0.16, icon: '🏙️', via: { map: 'station', id: 'stTicket' } },
  { id: 'airport', x: 0.44, y: 0.2, icon: '✈️', via: { map: 'taipei', id: 'ap_mrtTaipei' } },
  { id: 'hualien', x: 0.66, y: 0.46, icon: '🏔️', via: { map: 'station', id: 'stTicket' } },
  { id: 'tainan', x: 0.34, y: 0.68, icon: '🏮', via: { map: 'station', id: 'stTicket' } },
  { id: 'kaohsiung', x: 0.4, y: 0.8, icon: '⚓', via: { map: 'station', id: 'stTicket' } },
  { id: 'barcelona', x: 0.14, y: 0.14, icon: '🇪🇸', via: { map: 'airport', id: 'ap_checkin' } },
];

/** Which overview place the player is in right now (districts count as their city). */
export function placeOf(mapId: keyof typeof MAPS): Place['id'] {
  if (['ximen', 'longshan', 'cks', 'beitou', 'mrt', 'museum'].includes(mapId)) return 'taipei';
  if (mapId === 'hlcity' || mapId === 'hotel') return 'hualien';
  if (PLACES.some((p) => p.id === mapId)) return mapId;
  return 'town';
}
